import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeDrawer } from '@theme/theme-drawer';
import { StandardEvents } from '@shopify/events';

const AUTO_CLOSE_DELAY = 1000;
const DESKTOP_MEDIA = '(min-width: 750px)';
const POPOVER_OFFSET = 8;

/**
 * The empty-cart message, opened when the header cart icon is clicked while the cart is empty.
 *
 * Routes the header cart-icon click: an empty cart shows this message; a filled
 * cart (or an already-open drawer) falls through to the drawer's `toggle()`,
 * exactly like the previous `#cart-drawer/toggle` binding. On phones the message
 * is a modal bottom sheet; on desktop the same element is shown via the Popover
 * API as a light-dismiss popover anchored under the cart icon. Either way it
 * dismisses itself after a fixed delay — deliberately no pause on hover/touch.
 *
 * @extends {DialogComponent}
 */
class EmptyCartDialogComponent extends DialogComponent {
  /** @type {number | undefined} */
  #autoCloseTimer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#clearAutoClose);
    this.refs.dialog.addEventListener('toggle', this.#handlePopoverToggle);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearAutoClose();
    this.removeEventListener(DialogCloseEvent.eventName, this.#clearAutoClose);
    this.refs.dialog.removeEventListener('toggle', this.#handlePopoverToggle);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /**
   * Handles the header cart-icon click.
   *
   * The trigger is an `<a href="/cart">`. On desktop (≥750px) the cart is a
   * standalone page, so we let the link navigate natively. On mobile we
   * intercept it and open the drawer / empty-cart sheet instead.
   *
   * @param {Event} [event]
   */
  handleCartIconClick(event) {
    const isDesktop = window.matchMedia(DESKTOP_MEDIA).matches;
    const drawer = document.getElementById('cart-drawer');
    const cartIsEmpty = Boolean(document.querySelector('#cart-drawer .cart-drawer--empty'));

    // Desktop: the cart is a standalone page. A FILLED cart lets the
    // <a href="/cart"> navigate there. An EMPTY cart shows the small anchored
    // popover under the icon instead of loading a near-empty page.
    if (isDesktop) {
      if (!cartIsEmpty) return;
      event?.preventDefault();
      const { dialog } = this.refs;
      if (typeof dialog.showPopover === 'function') {
        this.#showPopover();
      } else {
        this.showDialog();
      }
      return;
    }

    // Mobile: intercept the link and keep the drawer / empty-cart sheet.
    event?.preventDefault();
    if (!cartIsEmpty || drawer?.hasAttribute('open')) {
      if (drawer instanceof ThemeDrawer) drawer.toggle();
      return;
    }
    this.showDialog();
  }

  showDialog() {
    super.showDialog();
    this.#startAutoClose();
  }

  /**
   * Desktop path: shows the element as a light-dismiss popover anchored
   * under the cart icon (click-outside and Escape are native to popover="auto").
   */
  #showPopover() {
    const { dialog } = this.refs;
    if (dialog.matches(':popover-open')) return;

    const trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      dialog.style.setProperty('--empty-cart-popover-top', `${Math.round(rect.bottom + POPOVER_OFFSET)}px`);
      dialog.style.setProperty('--empty-cart-popover-right', `${Math.round(window.innerWidth - rect.right)}px`);
    }

    dialog.showPopover();
    this.#startAutoClose();
  }

  #startAutoClose() {
    this.#clearAutoClose();
    this.#autoCloseTimer = window.setTimeout(this.#autoClose, AUTO_CLOSE_DELAY);
  }

  #clearAutoClose = () => {
    if (this.#autoCloseTimer) {
      window.clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = undefined;
    }
  };

  /**
   * Clears the timer when the popover light-dismisses (click-outside/Escape).
   *
   * @param {Event} event
   */
  #handlePopoverToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState === 'closed') this.#clearAutoClose();
  };

  /**
   * Dismisses whichever presentation is showing. Skips when a manual modal
   * close is already animating out — `dialog.open` stays true until the exit
   * animation finishes, so a re-entrant `closeDialog()` would restart it.
   */
  #autoClose = () => {
    this.#autoCloseTimer = undefined;
    const { dialog } = this.refs;
    if (dialog.matches(':popover-open')) {
      dialog.hidePopover();
      return;
    }
    if (!dialog.open || dialog.classList.contains('dialog-closing')) return;
    this.closeDialog();
  };

  /**
   * An add-to-cart landing while the message is showing means the cart is no
   * longer empty — dismiss so the drawer's auto-open doesn't stack underneath.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;
    const { dialog } = this.refs;
    if (dialog.matches(':popover-open')) {
      dialog.hidePopover();
    } else if (dialog.open) {
      this.closeDialog();
    }
  };
}

if (!customElements.get('empty-cart-dialog-component')) {
  customElements.define('empty-cart-dialog-component', EmptyCartDialogComponent);
}
